import { useEffect, useState, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Form } from "react-bootstrap";
import { Axios } from "../../../Api/axios";
import { ADMIN_SEND_NOTIFICATION, ADMIN_ALL_CLASSES, ALLSTD } from "../../../Api/Api";
import { useCounts } from "../../../context/CountsContext";
import DashDialog from "../../../Components/Dashboard/DashDialog";
import DashSelect from "../../../Components/Dashboard/DashSelect";
import LoadingSubmit from "../../../Components/Loading/Loading";
import "./notifications.css";

const TYPE_OPTIONS = [
  { value: "دورة مكثفة", label: "بدء دورة مكثفة" },
  { value: "تذكير عام", label: "تذكير عام" },
  { value: "إعلان", label: "إعلان" },
  { value: "أخرى", label: "أخرى" },
];

const TARGET_OPTIONS = [
  { value: "all", label: "جميع الطلاب" },
  { value: "class", label: "حسب الصف" },
  { value: "year", label: "حسب الدفعة (السنة)" },
  { value: "students", label: "طلاب محددون" },
];

export default function SendNotification() {
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [type, setType] = useState(TYPE_OPTIONS[0].value);
  const [targetType, setTargetType] = useState("all");

  const [classes, setClasses] = useState([]);
  const [classId, setClassId] = useState("");

  const [students, setStudents] = useState([]);
  const [studentsLoading, setStudentsLoading] = useState(false);
  const [year, setYear] = useState("");
  const [years, setYears] = useState([]);

  const [search, setSearch] = useState("");
  const [selectedIds, setSelectedIds] = useState(new Set());

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const nav = useNavigate();
  const { refreshCounts } = useCounts();

  useEffect(() => {
    Axios.get(ADMIN_ALL_CLASSES)
      .then((res) => setClasses(res.data.classes || res.data || []))
      .catch(() => {});
  }, []);

  // load students (for the "specific students" picker and the years dropdown)
  const fetchStudents = useCallback(() => {
    setStudentsLoading(true);
    Axios.get(`${ALLSTD}?limit=500&page=1`)
      .then((res) => {
        const list = res.data.students || [];
        setStudents(list);
        setYears([...new Set(list.map((s) => s.year).filter(Boolean))].sort());
      })
      .catch(() => {})
      .finally(() => setStudentsLoading(false));
  }, []);

  useEffect(() => {
    if (targetType === "students" && students.length === 0) fetchStudents();
    if (targetType === "year" && years.length === 0) fetchStudents();
  }, [targetType, students.length, years.length, fetchStudents]);

  const filteredStudents = useMemo(() => {
    if (!search) return students;
    const q = search.toLowerCase();
    return students.filter((s) => s.name.toLowerCase().includes(q) || s.phone?.includes(search));
  }, [students, search]);

  function toggleStudent(id) {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  const isValid =
    title.trim().length > 0 &&
    body.trim().length > 0 &&
    (targetType !== "class" || classId) &&
    (targetType !== "year" || year) &&
    (targetType !== "students" || selectedIds.size > 0);

  async function handleSubmit() {
    if (!isValid || loading) return;
    setLoading(true);
    setError("");
    try {
      const payload = { title: title.trim(), body: body.trim(), type, target_type: targetType };
      if (targetType === "class") payload.class_id = classId;
      if (targetType === "year") payload.year = year;
      if (targetType === "students") payload.student_ids = [...selectedIds];

      await Axios.post(ADMIN_SEND_NOTIFICATION, payload);
      refreshCounts();
      nav("/dashboard/notifications");
    } catch (err) {
      setError(err?.response?.data?.message || "حدث خطأ أثناء إرسال الإشعار");
      setLoading(false);
    }
  }

  return (
    <>
      {loading && <LoadingSubmit />}
      <DashDialog
        title="إرسال إشعار جديد"
        onSubmit={handleSubmit}
        submitDisabled={!isValid || loading}
        submitLabel={loading ? "جاري الإرسال..." : "إرسال الإشعار"}
      >
        <Form.Group className="mb-3">
          <Form.Label>عنوان الإشعار</Form.Label>
          <Form.Control
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            type="text"
            placeholder="مثال: بدء دورة مكثفة في مادة الرياضيات"
          />
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>نوع الإشعار</Form.Label>
          <DashSelect value={type} onChange={setType} options={TYPE_OPTIONS} />
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>نص الإشعار</Form.Label>
          <Form.Control
            value={body}
            onChange={(e) => setBody(e.target.value)}
            as="textarea"
            rows={3}
            placeholder="اكتب نص الإشعار الذي سيصل للطلاب..."
          />
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>الجهة المستهدفة</Form.Label>
          <DashSelect value={targetType} onChange={setTargetType} options={TARGET_OPTIONS} />
        </Form.Group>

        {targetType === "class" && (
          <Form.Group className="mb-3">
            <Form.Label>الصف</Form.Label>
            <DashSelect
              value={classId}
              onChange={setClassId}
              placeholder="اختر الصف"
              options={classes.map((c) => ({ value: c.id, label: c.name }))}
            />
          </Form.Group>
        )}

        {targetType === "year" && (
          <Form.Group className="mb-3">
            <Form.Label>الدفعة (السنة)</Form.Label>
            <DashSelect
              value={year}
              onChange={setYear}
              placeholder={studentsLoading ? "جاري التحميل..." : "اختر الدفعة"}
              options={years.map((y) => ({ value: y, label: y }))}
            />
          </Form.Group>
        )}

        {targetType === "students" && (
          <Form.Group className="mb-3">
            <Form.Label>
              الطلاب المحددون <span className="ntf-selected-count">({selectedIds.size} محدد)</span>
            </Form.Label>
            <input
              className="ntf-student-search"
              type="text"
              placeholder="ابحث باسم الطالب أو رقمه..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <div className="ntf-student-list">
              {studentsLoading ? (
                <div className="ntf-student-empty">جاري تحميل الطلاب...</div>
              ) : filteredStudents.length === 0 ? (
                <div className="ntf-student-empty">لا يوجد طلاب مطابقون</div>
              ) : (
                filteredStudents.map((s) => (
                  <label key={s.id} className="ntf-student-item">
                    <input
                      type="checkbox"
                      checked={selectedIds.has(s.id)}
                      onChange={() => toggleStudent(s.id)}
                    />
                    <span className="ntf-student-name">{s.name}</span>
                    <span className="ntf-student-meta">
                      {[s.Class?.name || s.class?.name, s.year].filter(Boolean).join(" · ")}
                    </span>
                  </label>
                ))
              )}
            </div>
          </Form.Group>
        )}

        {error && <div className="ntf-error">{error}</div>}
      </DashDialog>
    </>
  );
}
